import {
  FraudMatchedApplicant,
  FraudMatchedApplication,
  FraudMatchedApplicationParticipant,
} from './riskHitSummaryApi';

export type UnverifiableSectionsValue = 'Yes' | 'No';

export interface RiskHitSummaryRow {
  id: string;
  applicationId: string;
  applicationType: string;
  navigationAppType: string | null;
  navigationSubscriberId: string | null;
  sourceApplication: FraudMatchedApplication;
  createdDate: string;
  createdDateSortValue: number;
  applicantsDisplay: string;
  matchedApplicantsCount: number;
  matchedFields: string[];
  matchedFieldsDisplay: string;
  sectionTypes: string[];
  sectionTypesDisplay: string;
  riskLevel: UnverifiableSectionsValue;
}

const uniqNonEmpty = (values: Array<string | undefined | null>): string[] =>
  Array.from(
    new Set(
      values.filter(
        (value): value is string =>
          typeof value === 'string' && value.trim().length > 0
      )
    )
  );

const normalizeToken = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const normalizeRouteAppType = (value: unknown): string | null => {
  const normalized = String(value ?? '').trim();
  if (normalized === '1' || normalized === '2' || normalized === '3') {
    return normalized;
  }
  return null;
};

const normalizeSubscriberId = (value: unknown): string | null => {
  const normalized = String(value ?? '').trim();
  return normalized.length > 0 ? normalized : null;
};

const mapApplicationTypeToRouteAppType = (value: unknown): string | null => {
  const normalized = normalizeToken(String(value ?? ''));

  if (
    normalized === '1' ||
    normalized === 'ats' ||
    normalized === 'application'
  ) {
    return '1';
  }

  if (
    normalized === '2' ||
    normalized === 'vr' ||
    normalized === 'docupload' ||
    normalized === 'docanalysis' ||
    normalized === 'documentupload'
  ) {
    return '2';
  }

  if (
    normalized === '3' ||
    normalized === 'guestcard' ||
    normalized === 'guest'
  ) {
    return '3';
  }

  return null;
};

const getNavigationAppType = (
  application: FraudMatchedApplication
): string | null => {
  const withNavigationHints = application as FraudMatchedApplication & {
    appInvitationType?: unknown;
    app_invitation_type?: unknown;
    appType?: unknown;
    applicationTypeId?: unknown;
    ApplicationType?: unknown;
  };

  const explicitCandidates = [
    withNavigationHints.appInvitationType,
    withNavigationHints.app_invitation_type,
    withNavigationHints.appType,
    withNavigationHints.applicationTypeId,
  ];

  for (const candidate of explicitCandidates) {
    const explicitAppType = normalizeRouteAppType(candidate);
    if (explicitAppType) {
      return explicitAppType;
    }
  }

  const typeCandidates = [
    withNavigationHints.applicationType,
    withNavigationHints.ApplicationType,
  ];

  for (const candidate of typeCandidates) {
    const mappedAppType = mapApplicationTypeToRouteAppType(candidate);
    if (mappedAppType) {
      return mappedAppType;
    }
  }

  return null;
};

const getDisplayApplicationType = (
  application: FraudMatchedApplication,
  navigationAppType: string | null
): string => {
  const normalizedApplicationType = String(application?.applicationType ?? '')
    .trim();
  if (normalizedApplicationType.length > 0) {
    return normalizedApplicationType;
  }

  void navigationAppType;

  return 'Unknown';
};

const getNavigationSubscriberId = (
  application: FraudMatchedApplication
): string | null => {
  const withNavigationHints = application as FraudMatchedApplication & {
    SubscriberID?: unknown;
    subscriberID?: unknown;
    subscriberId?: unknown;
    subscriberid?: unknown;
  };

  const candidates = [
    withNavigationHints.SubscriberID,
    withNavigationHints.subscriberID,
    withNavigationHints.subscriberId,
    withNavigationHints.subscriberid,
  ];

  for (const candidate of candidates) {
    const subscriberId = normalizeSubscriberId(candidate);
    if (subscriberId) {
      return subscriberId;
    }
  }

  return null;
};

const mapMatchedFieldLabel = (value: string): string | null => {
  const normalized = normalizeToken(value);

  if (
    normalized === 'name' ||
    normalized.includes('fullname') ||
    normalized.includes('firstname') ||
    normalized.includes('lastname') ||
    normalized.includes('namematch') ||
    normalized.includes('applicantname')
  ) {
    return 'Full Name';
  }

  if (
    normalized === 'id' ||
    normalized.includes('iddocumentnumber') ||
    normalized.includes('drivinglicense') ||
    normalized.includes('driverlicense') ||
    normalized.includes('passport')
  ) {
    return 'ID';
  }

  if (
    normalized === 'dobselfreported' ||
    normalized.includes('dobselfreported') ||
    normalized.includes('applicantdob')
  ) {
    return 'DOB Self Reported';
  }

  if (
    normalized === 'dobmanual' ||
    normalized.includes('dobmanual') ||
    normalized.includes('identificationdob')
  ) {
    return 'DOB Manual';
  }

  if (
    normalized === 'dobocr' ||
    normalized.includes('dobocr') ||
    normalized.includes('ocrdob')
  ) {
    return 'DOB OCR';
  }

  if (
    normalized === 'dobbarcode' ||
    normalized.includes('dobbarcode') ||
    normalized.includes('barcodedob')
  ) {
    return 'DOB Barcode';
  }

  if (normalized.includes('email')) {
    return 'Email';
  }

  if (normalized.includes('fullname')) {
    return 'Full Name';
  }

  if (normalized.includes('phone') || normalized.includes('mobile')) {
    return 'Phone';
  }

  if (normalized.includes('ssn') || normalized.includes('socialsecurity')) {
    return 'SSN';
  }

  return null;
};

const mapSectionTypeLabel = (value: string): string | null => {
  const normalized = normalizeToken(value);

  if (normalized === 'id' || normalized.includes('identity')) {
    return 'ID';
  }

  if (normalized.includes('ssn') || normalized.includes('socialsecurity')) {
    return 'Social Security';
  }

  if (
    normalized.includes('paystub') ||
    normalized.includes('employment') ||
    normalized.includes('income') ||
    normalized.includes('employer')
  ) {
    return 'Employment Income';
  }

  if (normalized.includes('bank')) {
    return 'Banking';
  }

  return null;
};

const mapToAllowedLabels = (
  values: string[],
  mapper: (value: string) => string | null
): string[] =>
  Array.from(
    new Set(
      values
        .map((value) => mapper(value))
        .filter((value): value is string => Boolean(value))
    )
  );

const normalizeDobValueForComparison = (value: string): string => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    return '';
  }

  const yyyyMmDdMatch = trimmed.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (yyyyMmDdMatch) {
    const [, y, m, d] = yyyyMmDdMatch;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  const mmDdYyyyMatch = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (mmDdYyyyMatch) {
    const [, m, d, y] = mmDdYyyyMatch;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  return trimmed.toLowerCase();
};

const shouldSuppressDobManualFromValues = (
  matchedApplicants: FraudMatchedApplicant[]
): boolean => {
  const manualDobValues: string[] = [];
  const strongerDobValues: string[] = [];

  matchedApplicants.forEach((matchedApplicant) => {
    const matchedValues = matchedApplicant?.matchedValues || {};
    Object.entries(matchedValues).forEach(([key, rawValue]) => {
      if (typeof rawValue !== 'string') {
        return;
      }
      const label = mapMatchedFieldLabel(key);
      if (label === 'DOB Manual') {
        manualDobValues.push(normalizeDobValueForComparison(rawValue));
      } else if (label === 'DOB OCR' || label === 'DOB Barcode') {
        strongerDobValues.push(normalizeDobValueForComparison(rawValue));
      }
    });
  });

  if (!manualDobValues.length || !strongerDobValues.length) {
    return false;
  }

  const strongerSet = new Set(strongerDobValues.filter(Boolean));
  return manualDobValues.some((value) => value && strongerSet.has(value));
};

const toNormalizedLabelList = (values: unknown): string[] => {
  if (!Array.isArray(values)) {
    return [];
  }

  return uniqNonEmpty(
    values.map((value) => (typeof value === 'string' ? value : undefined))
  );
};

const hasAnyFraudProfileIds = (
  matchedApplicants: FraudMatchedApplicant[]
): boolean =>
  matchedApplicants.some(
    (applicant) =>
      Array.isArray(applicant?.fraudProfileIds) &&
      applicant.fraudProfileIds.length > 0
  );

const toTimestamp = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1_000_000_000_000 ? value * 1000 : value;
  }

  if (typeof value === 'string' && value.trim()) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric < 1_000_000_000_000 ? numeric * 1000 : numeric;
    }
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return null;
};

const createdDatePartsFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const formatUsDate = (value: Date): string => {
  const parts = createdDatePartsFormatter.formatToParts(value);
  const partMap = parts.reduce<Record<string, string>>((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});

  const month = partMap.month || '00';
  const day = partMap.day || '00';
  const year = partMap.year || '0000';

  return `${month}/${day}/${year}`;
};

const getCreatedDate = (
  application: FraudMatchedApplication
): { createdDate: string; createdDateSortValue: number } => {
  const withTimestampFields = application as FraudMatchedApplication & {
    createdDate?: unknown;
    createdOn?: unknown;
    createdAt?: unknown;
    applicationCreatedDate?: unknown;
    applicationCreatedOn?: unknown;
    appCreatedDate?: unknown;
    appCreatedOn?: unknown;
  };

  const rawTimestamp =
    withTimestampFields.createdAt ??
    withTimestampFields.applicationCreatedOn ??
    withTimestampFields.createdDate ??
    withTimestampFields.createdOn ??
    withTimestampFields.applicationCreatedDate ??
    withTimestampFields.appCreatedDate ??
    withTimestampFields.appCreatedOn;

  const timestamp = toTimestamp(rawTimestamp);
  if (timestamp === null) {
    return {
      createdDate: '\u2014',
      createdDateSortValue: Number.NEGATIVE_INFINITY,
    };
  }

  return {
    createdDate: formatUsDate(new Date(timestamp)),
    createdDateSortValue: timestamp,
  };
};

const normalizeNamePart = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const composeDisplayName = (
  participant: FraudMatchedApplicationParticipant
): string | null => {
  const fullName = normalizeNamePart(participant?.fullName);
  if (fullName) {
    return fullName;
  }

  const firstName = normalizeNamePart(participant?.firstName);
  const middleName = normalizeNamePart(participant?.middleName);
  const lastName = normalizeNamePart(participant?.lastName);
  const compositeName = [firstName, middleName, lastName]
    .filter((token): token is string => Boolean(token))
    .join(' ');

  return compositeName || null;
};

const getApplicantsDisplay = (
  application: FraudMatchedApplication,
  matchedApplicants: FraudMatchedApplicant[]
): string => {
  const apiApplicantsDisplay = normalizeNamePart(application?.applicantsDisplay);
  if (apiApplicantsDisplay) {
    return apiApplicantsDisplay;
  }

  const participants = Array.isArray(application?.participants)
    ? application.participants
    : [];
  if (participants.length > 0) {
    const dedupedNames = uniqNonEmpty(
      participants.map((participant) => composeDisplayName(participant))
    );
    if (dedupedNames.length > 0) {
      return dedupedNames.join(', ');
    }
  }

  const fallbackNames = uniqNonEmpty(
    matchedApplicants.flatMap((matchedApplicant) => {
      const fullName = matchedApplicant?.matchedValues?.fullName;
      return typeof fullName === 'string' ? [fullName] : [];
    })
  );
  if (fallbackNames.length > 0) {
    return fallbackNames.join(', ');
  }

  return '\u2014';
};

const getMatchedOnLabels = (
  application: FraudMatchedApplication,
  matchedApplicants: FraudMatchedApplicant[]
): string[] => {
  const sortLabelsAsc = (labels: string[]): string[] =>
    [...labels].sort((left, right) =>
      left.localeCompare(right, undefined, { sensitivity: 'base' })
    );

  const suppressDobManualByValue = (labels: string[]): string[] => {
    const hasDobManual = labels.includes('DOB Manual');
    const hasStrongerDob = labels.includes('DOB OCR') || labels.includes('DOB Barcode');
    if (!hasDobManual || !hasStrongerDob) {
      return labels;
    }
    if (!shouldSuppressDobManualFromValues(matchedApplicants)) {
      return labels;
    }
    return labels.filter((label) => label !== 'DOB Manual');
  };

  const fromApi = toNormalizedLabelList(application?.matchedOn);
  const matchedFields = uniqNonEmpty(
    matchedApplicants.flatMap((matchedApplicant) =>
      Array.isArray(matchedApplicant?.matchedFields)
        ? matchedApplicant.matchedFields
        : []
    )
  );

  const baseLabels =
    fromApi.length > 0
      ? uniqNonEmpty([
          ...mapToAllowedLabels(fromApi, mapMatchedFieldLabel),
          ...fromApi.filter((value) => mapMatchedFieldLabel(value) === null),
        ])
      : mapToAllowedLabels(matchedFields, mapMatchedFieldLabel);

  const hasNameFieldSignal = matchedFields.some(
    (field) => mapMatchedFieldLabel(field) === 'Full Name'
  );
  const hasDobFieldSignal = matchedFields.some(
    (field) => mapMatchedFieldLabel(field)?.startsWith('DOB')
  );

  let labels = baseLabels;

  if (hasNameFieldSignal && !labels.includes('Full Name')) {
    labels = [...labels, 'Full Name'];
  }

  if (hasDobFieldSignal) {
    const nestedDobLabels = mapToAllowedLabels(matchedFields, mapMatchedFieldLabel)
      .filter((label) => label.startsWith('DOB'));
    nestedDobLabels.forEach((label) => {
      if (!labels.includes(label)) {
        labels = [...labels, label];
      }
    });
  }

  return sortLabelsAsc(suppressDobManualByValue(labels));
};

const getSectionLabels = (
  application: FraudMatchedApplication,
  matchedApplicants: FraudMatchedApplicant[]
): string[] => {
  const fromApi = toNormalizedLabelList(application?.sections);
  if (fromApi.length > 0) {
    return fromApi;
  }

  const sectionTypes = uniqNonEmpty(
    matchedApplicants.flatMap((matchedApplicant) =>
      Array.isArray(matchedApplicant?.sectionTypes)
        ? matchedApplicant.sectionTypes
        : []
    )
  );

  return mapToAllowedLabels(sectionTypes, mapSectionTypeLabel);
};

const getRiskLevel = (
  application: FraudMatchedApplication,
  matchedApplicants: FraudMatchedApplicant[]
): UnverifiableSectionsValue => {
  const normalized = String(application?.riskLevel || '')
    .trim()
    .toLowerCase();

  if (normalized === 'yes') {
    return 'Yes';
  }

  if (normalized === 'no') {
    return 'No';
  }

  return hasAnyFraudProfileIds(matchedApplicants) ? 'Yes' : 'No';
};

export const buildRiskHitSummaryRows = (
  matchedApplications: FraudMatchedApplication[] = []
): RiskHitSummaryRow[] =>
  matchedApplications.map((application, index) => {
    const matchedApplicants = Array.isArray(application?.matchedApplicants)
      ? application.matchedApplicants
      : [];
    const matchedOn = getMatchedOnLabels(application, matchedApplicants);
    const sections = getSectionLabels(application, matchedApplicants);
    const { createdDate, createdDateSortValue } = getCreatedDate(application);
    const applicantsDisplay = getApplicantsDisplay(application, matchedApplicants);
    const navigationAppType = getNavigationAppType(application);

    return {
      id: `${String(application?.applicationId || 'unknown')}-${index}`,
      applicationId: String(application?.applicationId || ''),
      applicationType: getDisplayApplicationType(application, navigationAppType),
      navigationAppType,
      navigationSubscriberId: getNavigationSubscriberId(application),
      sourceApplication: application,
      createdDate,
      createdDateSortValue,
      applicantsDisplay,
      matchedApplicantsCount: matchedApplicants.length,
      matchedFields: matchedOn,
      matchedFieldsDisplay: matchedOn.join(', '),
      sectionTypes: sections,
      sectionTypesDisplay: sections.join(', '),
      riskLevel: getRiskLevel(application, matchedApplicants),
    };
  });

